import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Users, UserPlus, Pencil, Ban, RotateCcw, KeyRound, Trash2,
  Loader2, ShieldCheck, ShieldOff, X, CheckCircle2,
} from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ROLE_OPTIONS = [
  { value: 'admin',    label: 'Administrator' },
  { value: 'director', label: 'Director' },
  { value: 'broker',   label: 'Broker' },
  { value: 'credit',   label: 'Credit Team' },
  { value: 'assistant', label: 'Assistant' },
  { value: 'viewer',   label: 'Viewer' },
];

/**
 * User Account Management panel — inserted into the Integrations Command
 * Centre so admins can create, edit, deactivate/reactivate and reset
 * passwords for user accounts without leaving the dashboard.
 *
 * Admin-only. Uses existing backend endpoints:
 *   GET    /api/admin/users
 *   POST   /api/admin/users
 *   PATCH  /api/admin/users/{id}
 *   DELETE /api/admin/users/{id}
 */
export default function UserAccountsPanel() {
  const { token, user } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = user?.role === 'admin';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pwResetId, setPwResetId] = useState(null);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/users`, { headers: auth });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      // Sort by name (case-insensitive), active first
      data.sort((a, b) => {
        if ((b.is_active ?? true) !== (a.is_active ?? true)) return (b.is_active ?? true) ? 1 : -1;
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      });
      setUsers(data);
    } catch (e) {
      toast.error(`Failed to load users — ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [auth, isAdmin]);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin) {
    return (
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="user-accounts-panel-restricted">
        <div className="flex items-center gap-2 text-amber-200">
          <ShieldOff className="w-5 h-5" />
          <span className="text-sm font-bold">Admin-only section</span>
        </div>
        <p className="text-xs text-slate-300 mt-1">Only administrators can manage user accounts.</p>
      </div>
    );
  }

  const activeCount = users.filter(u => u.is_active !== false).length;
  const inactiveCount = users.length - activeCount;

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-5" data-testid="user-accounts-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <Users className="w-5 h-5 text-cyan-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
              User Account Management
              <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-200">Admin</span>
            </h3>
            <div className="text-[11px] text-slate-400">
              {users.length} total · <span className="text-emerald-300">{activeCount} active</span>
              {inactiveCount > 0 && <> · <span className="text-rose-300">{inactiveCount} deactivated</span></>}
            </div>
          </div>
        </div>
        <Button
          onClick={() => setShowCreate(v => !v)}
          className="bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-100 border border-cyan-500/30 h-8 text-xs"
          data-testid="user-create-toggle"
        >
          <UserPlus className="w-3.5 h-3.5 mr-1.5" /> {showCreate ? 'Cancel' : 'Create New Account'}
        </Button>
      </div>

      {showCreate && (
        <CreateAccountForm
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {loading ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading users…
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/50 text-[10px] font-black uppercase tracking-wider text-slate-400">
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Username / Email</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <UserRow
                  key={u.id}
                  u={u}
                  editing={editingId === u.id}
                  onEdit={() => { setEditingId(u.id === editingId ? null : u.id); setPwResetId(null); }}
                  resetting={pwResetId === u.id}
                  onResetPasswordToggle={() => { setPwResetId(pwResetId === u.id ? null : u.id); setEditingId(null); }}
                  onSaved={() => { setEditingId(null); setPwResetId(null); load(); }}
                  currentUserId={user?.id}
                />
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-slate-500 italic">No user accounts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateAccountForm({ onCreated }) {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'broker', phone: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error('Name, username/email and password are required');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create user');
      toast.success(`User ${form.name} created`);
      onCreated?.(data.user);
    } catch (e) {
      toast.error(`Create failed — ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-4" data-testid="user-create-form">
      <div className="flex items-center gap-2 mb-3">
        <UserPlus className="w-4 h-4 text-cyan-300" />
        <span className="text-[11px] font-black uppercase tracking-widest text-cyan-200">Create new account</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        <Field label="Full name *">
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Jane Broker"
            className="h-9 bg-slate-900 text-slate-100 border-slate-700"
            data-testid="new-user-name"
          />
        </Field>
        <Field label="Username / email *" hint="Used to log in">
          <Input
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="jane@derwent or jane.broker"
            className="h-9 bg-slate-900 text-slate-100 border-slate-700"
            data-testid="new-user-email"
          />
        </Field>
        <Field label="Password * (min 6 chars)">
          <Input
            type="text"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="TemporaryPassword2026!"
            className="h-9 bg-slate-900 text-slate-100 border-slate-700 font-mono text-xs"
            data-testid="new-user-password"
          />
        </Field>
        <Field label="User role *">
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="h-9 bg-slate-900 text-slate-100 border-slate-700" data-testid="new-user-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Phone (optional)">
          <Input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="04XX XXX XXX"
            className="h-9 bg-slate-900 text-slate-100 border-slate-700"
            data-testid="new-user-phone"
          />
        </Field>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="text-[11px] text-slate-400">New accounts are set to <b>Active</b> by default and require MFA enrolment on first login.</p>
        <Button
          type="submit"
          disabled={busy}
          className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-bold h-9 text-xs"
          data-testid="new-user-submit"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <UserPlus className="w-3.5 h-3.5 mr-1.5" />}
          {busy ? 'Creating…' : 'Create Account'}
        </Button>
      </div>
    </form>
  );
}

function UserRow({ u, editing, onEdit, resetting, onResetPasswordToggle, onSaved, currentUserId }) {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isSelf = u.id === currentUserId;
  const isActive = u.is_active !== false;

  const patch = async (payload, successMsg) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/users/${u.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Update failed');
      toast.success(successMsg || 'Updated');
      onSaved?.();
    } catch (e) {
      toast.error(`Update failed — ${e.message}`);
    }
  };

  const toggleActive = async () => {
    if (isSelf && isActive) {
      toast.error('You cannot deactivate your own account');
      return;
    }
    if (!confirm(isActive
      ? `Deactivate ${u.name}? They will not be able to log in until reactivated.`
      : `Reactivate ${u.name}? They will be able to log in again.`)) return;
    await patch({ is_active: !isActive }, isActive ? `${u.name} deactivated` : `${u.name} reactivated`);
  };

  const remove = async () => {
    if (isSelf) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!confirm(`Permanently delete ${u.name}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/admin/users/${u.id}`, { method: 'DELETE', headers: auth });
      if (!res.ok) throw new Error((await res.json()).detail || 'Delete failed');
      toast.success(`${u.name} deleted`);
      onSaved?.();
    } catch (e) {
      toast.error(`Delete failed — ${e.message}`);
    }
  };

  return (
    <>
      <tr className="border-b border-slate-800 last:border-0 hover:bg-slate-900/30" data-testid={`user-row-${u.id}`}>
        <td className="px-3 py-2 text-slate-100 font-semibold">
          {u.name || '—'}
          {isSelf && <span className="ml-1.5 text-[9px] font-bold px-1 py-0.5 rounded bg-cyan-500/20 text-cyan-200">You</span>}
        </td>
        <td className="px-3 py-2 text-slate-300 font-mono text-xs">{u.email}</td>
        <td className="px-3 py-2">
          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-slate-200">
            {ROLE_OPTIONS.find(r => r.value === u.role)?.label || u.role}
          </span>
        </td>
        <td className="px-3 py-2">
          {isActive ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              <ShieldCheck className="w-2.5 h-2.5" /> Active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-500/15 text-rose-300 border border-rose-500/30">
              <ShieldOff className="w-2.5 h-2.5" /> Deactivated
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-slate-400 text-xs tabular-nums">
          {u.created_at ? new Date(u.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
        </td>
        <td className="px-3 py-2 text-right">
          <div className="inline-flex items-center gap-0.5">
            <IconButton icon={Pencil} label="Edit" onClick={onEdit} tone="cyan" testid={`user-edit-${u.id}`} />
            <IconButton icon={KeyRound} label="Reset password" onClick={onResetPasswordToggle} tone="amber" testid={`user-reset-pw-${u.id}`} />
            <IconButton
              icon={isActive ? Ban : RotateCcw}
              label={isActive ? 'Deactivate' : 'Reactivate'}
              onClick={toggleActive}
              tone={isActive ? 'rose' : 'emerald'}
              disabled={isSelf && isActive}
              testid={`user-toggle-${u.id}`}
            />
            <IconButton icon={Trash2} label="Delete" onClick={remove} tone="rose" disabled={isSelf} testid={`user-delete-${u.id}`} />
          </div>
        </td>
      </tr>
      {editing && (
        <tr data-testid={`user-edit-form-${u.id}`}>
          <td colSpan={6} className="px-3 py-3 bg-slate-950/60 border-b border-slate-800">
            <EditForm u={u} onSave={patch} onCancel={onEdit} />
          </td>
        </tr>
      )}
      {resetting && (
        <tr data-testid={`user-reset-form-${u.id}`}>
          <td colSpan={6} className="px-3 py-3 bg-slate-950/60 border-b border-slate-800">
            <ResetPasswordForm onSave={(pw) => patch({ password: pw }, 'Password reset — new credentials issued')} onCancel={onResetPasswordToggle} />
          </td>
        </tr>
      )}
    </>
  );
}

function EditForm({ u, onSave, onCancel }) {
  const [form, setForm] = useState({ name: u.name || '', email: u.email || '', role: u.role || 'broker', phone: u.phone || '' });
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Edit user</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="h-8 bg-slate-900 text-slate-100 border-slate-700 text-xs" data-testid={`edit-name-${u.id}`} />
        <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Username / email" className="h-8 bg-slate-900 text-slate-100 border-slate-700 text-xs font-mono" data-testid={`edit-email-${u.id}`} />
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger className="h-8 bg-slate-900 text-slate-100 border-slate-700 text-xs" data-testid={`edit-role-${u.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="h-8 bg-slate-900 text-slate-100 border-slate-700 text-xs" data-testid={`edit-phone-${u.id}`} />
      </div>
      <div className="mt-2 flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onCancel} className="h-8 text-xs bg-transparent border-slate-700 text-slate-300" data-testid={`edit-cancel-${u.id}`}>
          <X className="w-3 h-3 mr-1" /> Cancel
        </Button>
        <Button onClick={() => onSave(form, 'Saved')} className="h-8 text-xs bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-bold" data-testid={`edit-save-${u.id}`}>
          <CheckCircle2 className="w-3 h-3 mr-1" /> Save
        </Button>
      </div>
    </div>
  );
}

function ResetPasswordForm({ onSave, onCancel }) {
  const [pw, setPw] = useState('');
  const canSubmit = pw.length >= 6;
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-amber-300 mb-2">Reset password</div>
      <div className="flex items-center gap-2">
        <Input
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="New password (min 6 characters)"
          className="h-8 bg-slate-900 text-slate-100 border-slate-700 text-xs font-mono flex-1"
          data-testid="reset-pw-input"
        />
        <Button variant="outline" onClick={onCancel} className="h-8 text-xs bg-transparent border-slate-700 text-slate-300" data-testid="reset-pw-cancel">
          <X className="w-3 h-3 mr-1" /> Cancel
        </Button>
        <Button
          onClick={() => canSubmit && onSave(pw)}
          disabled={!canSubmit}
          className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
          data-testid="reset-pw-save"
        >
          <KeyRound className="w-3 h-3 mr-1" /> Set new password
        </Button>
      </div>
      <p className="text-[10px] text-slate-500 mt-1.5">The new password is set immediately. Deliver it to the user through a secure channel.</p>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-slate-500 mt-0.5 block">{hint}</span>}
    </label>
  );
}

function IconButton({ icon: Icon, label, onClick, tone = 'cyan', disabled, testid }) {
  const toneMap = {
    cyan: 'text-cyan-300 hover:bg-cyan-500/20',
    amber: 'text-amber-300 hover:bg-amber-500/20',
    rose: 'text-rose-300 hover:bg-rose-500/20',
    emerald: 'text-emerald-300 hover:bg-emerald-500/20',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`p-1.5 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${toneMap[tone]}`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}
